import { buildCategoryPath } from "./path-utils";

export type SearchHitType = "post" | "point" | "category" | "tag" | "highlight";

export type SearchHit = {
  id: string;
  title: string;
  uri: string;
  type: SearchHitType;
  excerpt?: string;
  image?: string | null;
  /** Real slug for linking (never a GraphQL relay id). */
  slug?: string;
};

type CategoryNode = {
  slug: string;
  databaseId: number;
  parentDatabaseId?: number | null;
};

/** Lowercase + strip combining marks so "drazdiak" matches "Draždiak". */
export function foldSearchText(value: string): string {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Gatsby category archive path — ignore WP uri (often /category/<relayId>/). */
export function categoryArchivePath(
  category: CategoryNode,
  allCategories: CategoryNode[] = []
): string {
  const built = buildCategoryPath(category, allCategories);
  if (!built) return "/";
  return `/${built}/`.replace(/\/+/g, "/");
}

export function tagArchivePath(slug: string): string {
  const s = (slug || "").replace(/^\/+|\/+$/g, "");
  return s ? `/tag/${s}/` : "/tag/";
}

/** Build a navigable path for a search hit. */
export function resolveSearchHitPath(
  hit: {
    uri: string;
    type: SearchHitType;
    slug?: string;
    categories?: { nodes: CategoryNode[] } | null;
    /** Category node fields when type === category */
    databaseId?: number;
    parentDatabaseId?: number | null;
  },
  allCategories: CategoryNode[] = []
): string {
  if (hit.type === "category") {
    const slug = hit.slug;
    if (slug && hit.databaseId != null) {
      return categoryArchivePath(
        {
          slug,
          databaseId: hit.databaseId,
          parentDatabaseId: hit.parentDatabaseId ?? 0,
        },
        allCategories
      );
    }
    if (slug) return `/${slug}/`.replace(/\/+/g, "/");
  }
  if (hit.type === "tag") {
    return tagArchivePath(hit.slug || hit.uri.replace(/^\/?tag\//, ""));
  }
  if (hit.type === "highlight") {
    return hit.uri.startsWith("/") ? hit.uri : `/${hit.uri}`;
  }
  const cats = hit.categories?.nodes || [];
  if (cats.length > 0) {
    const path = `/${buildCategoryPath(cats[0], allCategories)}${hit.uri}`.replace(
      /\/+/g,
      "/"
    );
    return path.endsWith("/") ? path : `${path}/`;
  }
  return hit.uri.startsWith("/") ? hit.uri : `/${hit.uri}`;
}

/** Case- and diacritic-insensitive title/excerpt match; title hits rank first. */
export function filterSearchHits(
  hits: SearchHit[],
  query: string,
  limit = 100
): SearchHit[] {
  const q = foldSearchText(query.trim());
  if (!q) return [];
  const scored = hits
    .map((hit) => {
      const title = foldSearchText(hit.title || "");
      const excerpt = foldSearchText(hit.excerpt || "");
      const slug = foldSearchText(hit.slug || "");
      let score = 0;
      if (title === q || slug === q) score = 100;
      else if (title.startsWith(q) || slug.startsWith(q)) score = 80;
      else if (title.includes(q) || slug.includes(q)) score = 60;
      else if (excerpt.includes(q)) score = 30;
      else return null;
      return { hit, score };
    })
    .filter(Boolean) as Array<{ hit: SearchHit; score: number }>;

  scored.sort((a, b) => b.score - a.score || a.hit.title.localeCompare(b.hit.title));
  return scored.slice(0, limit).map((s) => s.hit);
}

export function searchResultsPath(query: string): string {
  const q = query.trim();
  if (!q) return "/search/";
  return `/search/?q=${encodeURIComponent(q)}`;
}
